import { useState } from "react"
import Pill from "@/components/Pill/Pill"
import RoundHeading from "@/components/RoundHeading/RoundHeading"
import Spinner from "@/components/Spinner/Spinner"
import PaperNote from "@/components/PaperNote/PaperNote"
import { PAINS_COPY } from "@/content/pains"
import { useDebouncedField } from "@/hooks/useDebouncedField"
import { useFirestoreWrite } from "@/hooks/useFirestoreWrite"
import { addPain, deletePain, updatePain } from "@/services/pains"
import { useSessionStore } from "@/store/sessionStore"
import {
  canAddPain,
  painPositions,
  painsBy,
  selectMyAuthorId,
  selectMyName,
  selectPains,
  selectSession,
} from "@/store/selectors"
import type { Pain } from "@/types"
import styles from "@/styles/pages/Pains.module.css"

/** The writes a button on this screen waits on — see `useFirestoreWrite`.
 *  Marking yourself done lives in `RoundControls` now, next to the timer. */
const ADD = "add"
const REMOVE = "remove"

/*
 * Silent writing.
 *
 * Two screens, and the difference between them is the whole exercise. Whoever is
 * writing sees only their own notes and is told so; whoever is watching sees who
 * has finished and nothing of what they wrote. If the watcher could read the
 * notes as they appeared, the room would converge on the first one — which is
 * the failure the round is designed around.
 *
 * The facilitator can write too — their notes carry their uid as `authorId` — but
 * they are not on the roster, so they are never part of "everyone is done".
 */

interface PainsWriteProps {
  canDrive: boolean
  /** The round is being read back. Nothing on this screen may be written. */
  readOnly: boolean
}

export default function PainsWrite({ canDrive, readOnly }: PainsWriteProps) {
  const session = useSessionStore(selectSession)
  const pains = useSessionStore(selectPains)
  const myAuthorId = useSessionStore(selectMyAuthorId)
  const myName = useSessionStore(selectMyName)

  if (!session) return null

  const mine = painsBy(pains, myAuthorId)
  const limit = session.config.painsPerParticipant
  const canAdd = canAddPain(pains, myAuthorId, limit)

  /*
   * The composer sits ABOVE your own notes, so this stack is not in board
   * order — the note you just finished is row 1 and the blank sheet is row 0.
   * Both take their tint from the board position, so finishing a note repaints
   * nothing: the first note keeps the first tint and the fresh sheet above it
   * takes the next one along.
   */
  const positions = painPositions(pains)

  const writingPanel = (
    <section className={styles.mine}>
      <p className={styles.private}>
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <rect
            x="3"
            y="7"
            width="10"
            height="7"
            rx="1.6"
            stroke="currentColor"
            strokeWidth="1.4"
          />
          <path d="M5.6 7V5.2a2.4 2.4 0 0 1 4.8 0V7" stroke="currentColor" strokeWidth="1.4" />
        </svg>
        {PAINS_COPY.writePrivate}
      </p>

      <div className={styles.notes}>
        {/* The blank note is gone rather than disabled while looking back: an
            empty box that refuses to take a word is a worse answer than no box. */}
        {canAdd && myAuthorId && !readOnly ? (
          <NewNote
            sessionId={session.id}
            authorId={myAuthorId}
            authorName={myName}
            // The position it is about to be created at: a pain is appended to
            // the board, so the blank sheet is already the colour it will keep.
            position={pains.length}
          />
        ) : null}

        {mine.map((pain) => (
          <MyNote
            key={pain.id}
            pain={pain}
            position={positions.get(pain.id) ?? 0}
            sessionId={session.id}
            readOnly={readOnly}
          />
        ))}
      </div>
    </section>
  )

  return (
    <div className={styles.write}>
      {/* The facilitator's screen is about the room; everyone else's is about
       * their own notes, so the heading is not the same question. */}
      {/*
        Two different people are being addressed. The facilitator is told what to
        watch for; the room is told how many notes it owes and what a good one
        looks like. Handing the room the facilitator's line was the bug here.
      */}
      <RoundHeading
        title={canDrive ? PAINS_COPY.watchTitle : PAINS_COPY.writeTitle}
        objective={canDrive ? PAINS_COPY.watchBody : PAINS_COPY.writeBrief(limit)}
      />
      {writingPanel}

      {/* Fixed above `RoundControls`' own capsule, same as the vote tray in
       * `PainsBoard` — centred inline within `.mine` would centre on that
       * column instead of the bar it is announcing the room is done writing
       * for. */}
      {!canAdd && !readOnly ? (
        <div className={styles.trayFixed}>
          <Pill tone="neutral">{PAINS_COPY.capHit(limit)}</Pill>
        </div>
      ) : null}
    </div>
  )
}

/*
 * The blank note at the top of your stack.
 *
 * It is tinted by the board position it is about to be created at, so the sheet
 * you are typing on is already the colour it will keep once it lands below.
 * The whole point of the exercise is that a note is a physical thing, and paper
 * does not change colour when you finish writing on it.
 */
function NewNote({
  sessionId,
  authorId,
  authorName,
  position,
}: {
  sessionId: string
  authorId: string
  authorName: string
  position: number
}) {
  const { write, isPending } = useFirestoreWrite()
  const [draft, setDraft] = useState("")

  /*
   * The position is followed while the sheet is blank and held the moment there
   * is a word on it. Everyone writes at once in this round, so `position` moves
   * whenever anyone else saves a note — and the paper under the pen repainting
   * because a stranger finished a sentence is exactly the thing the tint is
   * meant not to do. Re-synced by the clear in `commitDraft`, which is what
   * hands the next sheet a fresh colour.
   */
  const [slot, setSlot] = useState(position)
  if (draft === "" && slot !== position) setSlot(position)

  const commitDraft = () => {
    const text = draft.trim()
    if (!text) return
    // Cleared first, so the box is ready for the next note while this one is
    // still in flight — `onBlur` also commits, and a second call with an empty
    // draft is the no-op above rather than a duplicate write.
    setDraft("")
    write(() => addPain(sessionId, { text, authorId, authorName }), ADD)
  }

  return (
    <PaperNote position={slot}>
      <textarea
        className={styles.noteText}
        placeholder={PAINS_COPY.writePlaceholder}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        // Enter commits, so a note is one keystroke away. Shift keeps the
        // newline, for anyone who wants two lines.
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault()
            commitDraft()
          }
        }}
        onBlur={commitDraft}
      />
      <button
        type="button"
        className={styles.addNote}
        /* Kept pressable while a note is out: the draft has already been
         * cleared and a new id minted, so the next note is typed and added
         * against an empty box while the previous one is still landing. */
        disabled={!draft.trim()}
        aria-busy={isPending(ADD) || undefined}
        onClick={commitDraft}
      >
        {isPending(ADD) ? <Spinner size="sm" /> : null}
        {PAINS_COPY.addNote}
      </button>
    </PaperNote>
  )
}

/*
 * One of your own notes, editable. Keyed by pain id at the call site so the
 * debounced draft belongs to the note it was typed into — without that, deleting
 * a note in the middle would hand its pending text to whichever note slid up
 * into its place.
 */
function MyNote({
  pain,
  position,
  sessionId,
  readOnly,
}: {
  pain: Pain
  position: number
  sessionId: string
  readOnly: boolean
}) {
  const { write, isPending } = useFirestoreWrite()
  const field = useDebouncedField({
    value: pain.text,
    onCommit: (text) => write(() => updatePain(sessionId, pain.id, { text })),
  })
  const removing = isPending(REMOVE)

  return (
    <PaperNote position={position}>
      {/* `readOnly` rather than `disabled`: the note stays selectable, which is
          the one thing someone reading their own writing back wants to do with
          it. */}
      <textarea
        className={styles.noteText}
        placeholder={PAINS_COPY.editPlaceholder}
        readOnly={readOnly}
        value={field.value}
        onChange={field.onChange}
        onBlur={field.onBlur}
      />
      {/* One key is enough: the hook is called per note, so each note's remove
          button has its own pending state. */}
      {readOnly ? null : (
        <button
          type="button"
          className={styles.removeNote}
          title={PAINS_COPY.removeNote}
          aria-label={PAINS_COPY.removeNote}
          disabled={removing}
          aria-busy={removing || undefined}
          onClick={() => write(() => deletePain(sessionId, pain.id), REMOVE)}
        >
          {removing ? <Spinner size="sm" /> : "×"}
        </button>
      )}
    </PaperNote>
  )
}
